import { ActionIcon, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import { HAND_SUBTYPE_LABELS } from '@/data/handMetadata';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatValue } from '@/lib/formatValue';
import type { ChartData, HandData } from '@/types/chart';

interface HandDetailsPanelProps {
  hand: HandData;
  chartData: ChartData;
  onClose: () => void;
}

export default function HandDetailsPanel({ hand, chartData, onClose }: HandDetailsPanelProps) {
  const hasNoSegments = hand.segments.length === 0;
  const isMultiSegment = hand.segments.length > 1;
  const isMobile = useIsMobile();

  // Only treat as undefined if we have a categorical chart and the label is missing
  const isUndefined = hasNoSegments || (chartData.categoryMapping !== undefined && !hand.segments[0]?.label);

  const valueLabel = chartData.categoryMapping ? 'Strategy' : chartData.unit ? 'Value' : 'Equity';

  const categoryField = (
    <div>
      <Text size="sm" c="dimmed">
        Category
      </Text>
      <Text size="md">{HAND_SUBTYPE_LABELS[hand.subtype]}</Text>
    </div>
  );

  const valueField = isUndefined ? (
    <div>
      <Text size="sm" c="dimmed">
        {valueLabel}
      </Text>
      <Text size="md">Not in range</Text>
    </div>
  ) : isMultiSegment ? (
    <div>
      <Text size="sm" c="dimmed">
        {valueLabel}
      </Text>
      <Stack gap={4} mt={4}>
        {hand.segments.map((segment, index) => (
          <div key={segment.label || index} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text size="sm">{segment.label || 'Unknown'}</Text>
            <Text size="sm" fw={500}>
              {(segment.value * 100).toFixed(1)}%
            </Text>
          </div>
        ))}
      </Stack>
    </div>
  ) : (
    <div>
      <Text size="sm" c="dimmed">
        {valueLabel}
      </Text>
      <Text size="md">{formatValue(hand.segments[0].value, chartData.unit, hand.segments[0].label)}</Text>
    </div>
  );

  return (
    <Paper shadow="lg" p="sm" radius="md" onClick={(e) => e.stopPropagation()}>
      <Stack gap="sm">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title order={2}>{hand.hand}</Title>
          <ActionIcon variant="subtle" size="sm" onClick={onClose} aria-label="Close panel">
            <IconX size={16} />
          </ActionIcon>
        </div>

        {isMobile && !isMultiSegment ? (
          <SimpleGrid cols={2} spacing="sm">
            {categoryField}
            {valueField}
          </SimpleGrid>
        ) : (
          <>
            {categoryField}
            {valueField}
          </>
        )}
      </Stack>
    </Paper>
  );
}
